/* accruedPayroll.js -- THE ACCRUED-PAYROLL LINK (bi-weekly payroll, step 3b, 2026-10-08).
 *
 * A bi-weekly company that ACCRUES payroll carries a liability at each month-end: the wages earned and not yet paid
 * (salarySeries.accruedSalaryByMonth -- signed), plus the employer tax on them when the company's answer is
 * 'wages_and_taxes' (Brian B8 / Q2). computePayroll produces that series; this file is the ONE owner of how it reaches the
 * balance sheet, so the four parties that must agree read the same rules:
 *
 *   buildBSLines   wires the operator's linked account to the series       accruedPayrollLinkKey  (the rule)
 *   orchestrator   serves the series only where the builder wired it       accruedPayrollBuiltKey (asked of the BUILT skeleton)
 *   computeBS      UNCHANGED -- its module_link arm reads IS_REF['accrued_payroll'] for the wired leaf
 *   preflightCheck P21 e / f and P22 ask the same two questions
 *
 * No synthetic line is ever made: the account is one of the company's own balance-sheet lines (stored Step-4 role
 * 'accrued', with a label -- paySchedule.accruedLeafKeysFromMeta, Brian A3). Closed months stay the imported ledger; the
 * engine's figure starts with the first forecast month, and whatever else the close balance holds reverses through cash
 * in that month (Brian Q8 / A6 -- P22 names the amount).
 *
 * PURE. Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). Design: diffs/biweekly-design.txt 4.3, amended 4.4. */
import { isBiweekly, accruedLeafKeysFromMeta } from './paySchedule';

// The IS_REF key computeBS's module_link arm reads for the linked account.
export const ACCRUED_PAYROLL_REF = 'accrued_payroll';

// Does this company carry an accrued-payroll balance at all? Only bi-weekly + accrues. Nothing, null, never answered,
// semi-monthly and bi-weekly expensed-when-paid do not.
export function accruesPayroll(schedule) {
  return isBiweekly(schedule) && schedule.books === 'accrues';
}
// Does the balance include the employer tax on the accrued wages? (B8: 'wages_and_taxes')
export function accrualCoversTax(schedule) {
  return accruesPayroll(schedule) && schedule.accrualCovers === 'wages_and_taxes';
}

/* THE RULE the builder wires by. link = { schedule, lineKey } (lineKey = companies.accrued_payroll_bs_line_key).
   Returns the line key to wire, or null:
     the company accrues payroll, AND an account is linked, AND that key is one of THIS company's balance-sheet lines with
     stored role 'accrued' and a label, AND no other stream already links it (taken = the keys the revenue modules and the
     implementation lines wired -- the server refuses that state; a leaf is never wired twice). */
export function accruedPayrollLinkKey(link, bsActualsMeta, taken) {
  if (!link || !accruesPayroll(link.schedule)) return null;
  const key = link.lineKey;
  if (!key || typeof key !== 'string') return null;
  if (!accruedLeafKeysFromMeta(bsActualsMeta).includes(key)) return null;
  if (taken && key in taken) return null;
  return key;
}

// The id of the BUILT balance-sheet leaf that carries the accrued-payroll ref, or null. Asked of the skeleton the builder
// produced, so whoever serves or checks the series cannot disagree with the builder about where it lands.
export function accruedPayrollBuiltKey(bsLines) {
  const l = (bsLines || []).find(x => x && x.module_link_ref === ACCRUED_PAYROLL_REF);
  return l ? l.id : null;
}
